import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { withRouter, useHistory, RouteComponentProps } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../components/Common";
import { GovernorateDto, PaginateQuery } from "../../interfaces";
import { Paginated } from "../../models";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TextColumn,
} from "../../components";
import { ActionTypeEnum } from "../../enums";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditGovernorateModal } from "./AddEditGovernorateModal";
import {
  useGetGovernoratesQuery,
  useDeleteGovernorateMutation,
} from "../../store/api/governorateApi";

type TProps = RouteComponentProps & {};
const GovernoratesPageComponent: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  //#endregion

  //#region RTK Query
  const { data: response, isLoading: loading } =
    useGetGovernoratesQuery(paginateQuery);
  const [deleteGovernorate, { isLoading: deleteGovernorateLoading, error: deleteGovernorateError }] =
    useDeleteGovernorateMutation();
  //#endregion

  document.title = `${t("documentTitle", { pageName: "governorates" })}`;

  const columns: TableColumn<GovernorateDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC")
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC"
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string)
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteGovernorate(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {
    }
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<GovernorateDto>,
    sortDirection: string
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("governorates")} pageTitle={t("areas")} />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <div className="search-box">
                          <DebounceInput
                            className="form-control"
                            placeholder={`${t("searchPlaceholder")}...`}
                            minLength={2}
                            debounceTimeout={300}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => onSearch(event.target.value)}
                            value={paginateQuery.search}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button color="success" onClick={() => onAdd()}>
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", { item: "governorate" })}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        paginationTotalRows={response?.meta?.totalItems}
                      />
                    </Col>
                  </Row>
                  <DeleteModal
                    loading={deleteGovernorateLoading}
                    show={showDeleteModal}
                    onDeleteClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={deleteGovernorateError ? ((deleteGovernorateError as any)?.data?.message || "An error occurred") : undefined}
                  />
                  <AddEditGovernorateModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      setEditingItemId(null);
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export const GovernoratesPage = withRouter(GovernoratesPageComponent);
